import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { useTranslation } from '../../../common/hooks/useTranslation';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { GetSalesInvoicePaymentSummaryDto } from '../../../common/pokCore/contexts/SalesInvoicePaymentsContext';
import numberFormatter from '../../../common/numberFormatter';
import momencik from '../../../common/momencik';

interface SalesInvoicePaymentsSummaryProps {
  invoiceNumber: string;
}

export const SalesInvoicePaymentsSummary: React.FC<
  SalesInvoicePaymentsSummaryProps
> = props => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('fvsLng');

  const [summary, setSummary] =
    useState<GetSalesInvoicePaymentSummaryDto | null>(null);

  useEffect(() => {
    pok.salesInvoicePayments
      .getSummary(props.invoiceNumber)
      .then(setSummary)
      .catch(err => notifications.caughtError(err));
  }, [pok.salesInvoicePayments, props.invoiceNumber, notifications]);

  if (!summary) {
    return null;
  }

  const fmt = (value: number | null) => numberFormatter(value, true);

  const isOverdue =
    summary.paymentDueDate !== null &&
    new Date(summary.paymentDueDate) < new Date() &&
    (summary.currentBalance ?? 0) > 0;

  const overdueClass = isOverdue ? 'text-danger' : '';

  return (
    <div className="d-flex flex-wrap gap-4 px-1 pb-3 border-bottom mb-3">
      <div>
        <div className="text-muted small">
          {t(tk.finance.invoiceGrossAmount)}
        </div>
        <div className="fw-semibold">{fmt(summary.grossAmount)}</div>
      </div>
      <div>
        <div className="text-muted small">{t(tk.finance.totalPaidAmount)}</div>
        <div className="fw-semibold">{fmt(summary.totalPaid)}</div>
      </div>
      <div>
        <div className="text-muted small">
          {t(tk.finance.remainingBalance)}{' '}
          <OverlayTrigger
            overlay={<Tooltip>{t(tk.finance.remainingBalanceTooltip)}</Tooltip>}
          >
            <Icon.InfoCircle
              className="text-muted"
              style={{ cursor: 'help' }}
            />
          </OverlayTrigger>
        </div>
        <div className={`fw-semibold ${overdueClass}`}>
          {fmt(summary.currentBalance)}
        </div>
      </div>
      <div>
        <div className="text-muted small">
          {t(tk.finance.paymentDueDate)}{' '}
          <OverlayTrigger
            overlay={<Tooltip>{t(tk.finance.paymentDueDateTooltip)}</Tooltip>}
          >
            <Icon.InfoCircle
              className="text-muted"
              style={{ cursor: 'help' }}
            />
          </OverlayTrigger>
        </div>
        <div className={`fw-semibold ${overdueClass}`}>
          {summary.paymentDueDate ? momencik(summary.paymentDueDate) : '—'}
        </div>
      </div>
    </div>
  );
};
